import { OperationChain, Scenario } from '@/types';
import React from 'react';
import { ChainFilters } from '../scenarioSlice';
import { TagType } from '@/types';
import { MultiSelectBox } from '../../helpers/MultiSelect';
import { Tag } from '../../helpers/Tag';
import { getUniqueChainTsps } from '../../utils/chainUtils';

export const filterChains = (
  scenario: Scenario,
  chains: OperationChain[],
  filters: ChainFilters,
) => {
  const tspArray = Object.keys(filters.tsps).filter((tsp) => filters.tsps[tsp]);
  const ids = Object.keys(filters.ids).filter((id) => filters.ids[id]);
  const deliveryTspArray = Object.keys(filters.delivery).filter(
    (tsp) => filters.delivery[tsp],
  );
  const receiptTspArray = Object.keys(filters.receipt).filter(
    (tsp) => filters.receipt[tsp],
  );
  const tags = Object.keys(filters.tags).filter((tagId) => filters.tags[tagId]);
  return chains.filter((chain) => {
    const chainTsps = getUniqueChainTsps(scenario, chain);
    const receiptTsp = scenario.operations.find(
      (op) => op.id === chain.operations[0],
    )?.receipt_point?.tsp_name;
    const deliveryTsp = scenario.operations.find(
      (op) => op.id === chain.operations.slice(-1).pop(),
    )?.delivery_point?.tsp_name;

    const matchIds = ids.length === 0 || ids.includes(chain.id.toString());
    if (!matchIds) return false;

    if (
      tspArray.length == 0 &&
      deliveryTspArray.length == 0 &&
      receiptTspArray.length == 0 &&
      tags.length == 0
    ) {
      return true;
    }

    const includeTsps =
      tspArray.length === 0
        ? filters.conjunctive
        : filters.conjunctive
        ? tspArray.every((tsp) => chainTsps.includes(Number(tsp)))
        : tspArray.some((tsp) => chainTsps.includes(Number(tsp)));

    const matchDeliveryTsp =
      deliveryTspArray.length === 0
        ? filters.conjunctive
        : deliveryTspArray.some((tsp) => tsp === deliveryTsp);

    const matchReceiptTsp =
      receiptTspArray.length === 0
        ? filters.conjunctive
        : receiptTspArray.some((tsp) => tsp === receiptTsp);

    const includeTags =
      tags.length === 0
        ? filters.conjunctive
        : filters.conjunctive
        ? tags.every((tag) =>
            chain.chain_info.tags.some((t) => t.type.id.toString() === tag),
          )
        : tags.some((tag) =>
            chain.chain_info.tags.some((t) => t.type.id.toString() === tag),
          );

    const conditions = [
      includeTsps,
      matchDeliveryTsp,
      matchReceiptTsp,
      includeTags,
    ];

    const result = filters.conjunctive
      ? conditions.every((condition) => condition)
      : conditions.some((condition) => condition);

    return filters.negative ? !result : result;
  });
};

export type ChainFilterFormProps = {
  filter: ChainFilters;
  setFilter: (
    type: keyof ChainFilters,
    setterFunc: (currentFilters: ChainFilters) => ChainFilters,
  ) => void;
  tsps: Array<{ id: number; name: string }>;
  tags: TagType[];
};

export const ChainFilterForm: React.FC<ChainFilterFormProps> = ({
  filter,
  setFilter,
  tsps,
  tags,
}) => {
  return (
    <>
      <a
        className="btn btn-light w-100 dropdown-toggle my-0"
        id="filters-collapse-button"
        data-bs-auto-close="outside"
        data-bs-toggle="collapse"
        aria-expanded="false"
        href="#filters-collapse"
      >
        Filter Chains
      </a>
      <div id="filters-collapse" className="collapse w-100 px-2 mt-1">
        <div className="w-100">
          <MultiSelectBox
            labelWidth="126px"
            options={tsps}
            title="Pipelines"
            keyExtractor={(tsp) => `through-tsp-${tsp.id}`}
            isMarked={(tsp) => Boolean(filter.tsps[tsp.name])}
            allowAll
            onToggleAll={(checked) => {
              setFilter('tsps', (prev) => ({
                ...prev,
                tsps: tsps.reduce(
                  (acc, tsp) => ({
                    ...acc,
                    [tsp.name]: checked,
                  }),
                  {},
                ),
              }));
            }}
            onToggle={(tsp, checked) => {
              setFilter('tsps', (prev) => ({
                ...prev,
                tsps: {
                  ...prev.tsps,
                  [tsp.name]: checked,
                },
              }));
            }}
            optionRenderer={(tsp) => tsp.name}
          />
        </div>
        <div className="mt-1 w-100">
          <MultiSelectBox
            labelWidth="126px"
            options={tsps}
            title="Receipt from"
            keyExtractor={(tsp) => `receipt-tsp-${tsp.id}`}
            isMarked={(tsp) => Boolean(filter.receipt[tsp.name])}
            allowAll
            onToggleAll={(checked) => {
              setFilter('receipt', (prev) => ({
                ...prev,
                receipt: tsps.reduce(
                  (acc, tsp) => ({
                    ...acc,
                    [tsp.name]: checked,
                  }),
                  {},
                ),
              }));
            }}
            onToggle={(tsp, checked) => {
              setFilter('receipt', (prev) => ({
                ...prev,
                receipt: {
                  ...prev.receipt,
                  [tsp.name]: checked,
                },
              }));
            }}
            optionRenderer={(tsp) => tsp.name}
          />
        </div>
        <div className="mt-1 w-100">
          <MultiSelectBox
            labelWidth="126px"
            options={tsps}
            title="Delivery at"
            keyExtractor={(tsp) => `delivery-tsp-${tsp.id}`}
            isMarked={(tsp) => Boolean(filter.delivery[tsp.name])}
            allowAll
            onToggleAll={(checked) => {
              setFilter('delivery', (prev) => ({
                ...prev,
                delivery: tsps.reduce(
                  (acc, tsp) => ({
                    ...acc,
                    [tsp.name]: checked,
                  }),
                  {},
                ),
              }));
            }}
            onToggle={(tsp, checked) => {
              setFilter('delivery', (prev) => ({
                ...prev,
                delivery: {
                  ...prev.delivery,
                  [tsp.name]: checked,
                },
              }));
            }}
            optionRenderer={(tsp) => tsp.name}
          />
        </div>
        <div className="mt-1 w-100">
          <MultiSelectBox
            labelWidth="126px"
            options={tags}
            title="Tags"
            keyExtractor={(tag) => `tag-filter-${tag.id}`}
            isMarked={(tag) => Boolean(filter.tags[tag.id.toString()])}
            onToggle={(tag, checked) => {
              setFilter('tags', (prev) => ({
                ...prev,
                tags: {
                  ...prev.tags,
                  [tag.id.toString()]: checked,
                },
              }));
            }}
            optionRenderer={(tag) => <Tag name={tag.name} color={tag.color} />}
          />
        </div>
        <div className="mt-1 w-100 d-flex justify-content-center gap-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={filter.conjunctive}
              id="conjunctive-filter"
              onChange={(evt) =>
                setFilter('conjunctive', (prev) => ({
                  ...prev,
                  conjunctive: evt.target.checked,
                }))
              }
            />
            <label className="form-check-label" htmlFor="conjunctive-filter">
              Match All
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={filter.negative}
              id="negative-filter"
              onChange={(evt) =>
                setFilter('negative', (prev) => ({
                  ...prev,
                  negative: evt.target.checked,
                }))
              }
            />
            <label className="form-check-label" htmlFor="negative-filter">
              Negate Filters
            </label>
          </div>
        </div>
      </div>
      {Object.values(filter.ids).some((val) => val) && (
        <div className="d-flex justify-content-center">
          <span className="text-danger">
            Showing chains through comprehensive view filters
          </span>
        </div>
      )}
    </>
  );
};
